import { Controller } from "@hotwired/stimulus"
import { i18n } from "../../lib/i18n"

export default class extends Controller {
  static targets = [
    "emailInput", 
    "adminCheckbox", 
    "addButton", 
    "userRow", 
    "hiddenInputs", 
    "autoSaveIndicator"
  ]
  static values = { 
    autoSaveUrl: String, 
    autoSaveDelay: Number,
    initialUsers: Array,
    totalSeats: Number,
    currentUserEmail: String,
    existingNetwork: Boolean
  }

  private autoSaveTimeout: number | null = null
  private invitedUsers: Array<{
    email: string
    organization_admin: boolean
    network_admin: boolean
  }> = []

  connect() {
    try {
      // Load initial users from data attribute (more reliable than parsing DOM)
      if (this.hasInitialUsersValue && this.initialUsersValue.length > 0) {
        this.invitedUsers = this.initialUsersValue.map(user => ({
          email: user.email || user['email'] || '',
          organization_admin: user.organization_admin || user['organization_admin'] || false,
          network_admin: user.network_admin || user['network_admin'] || false
        })).filter(user => user.email)
      } else {
        // Fallback to loading from DOM
        this.loadExistingUsers()
      }

      this.updateHiddenInputs() // Initialize hidden inputs on connect
      this.setupAutoSave()
      this.updateUsersLeft()
      this.renderCurrentUser() // Render current user at the top
      
      // Ensure hidden inputs are updated before form submission
      const form = document.getElementById('onboarding-form')
      if (form) {
        form.addEventListener('submit', (e) => {
          this.updateHiddenInputs()
          // When there are no users, we still need to send invited_users to satisfy params.expect
          // We send a placeholder that will be filtered out by the interaction
          if (this.invitedUsers.length === 0) {
            const formElement = e.target as HTMLFormElement
            // Send a placeholder field that ensures invited_users exists
            // The interaction's normalize_invited_users will filter out empty strings
            const placeholderInput = document.createElement('input')
            placeholderInput.type = 'hidden'
            placeholderInput.name = 'onboarding_user_invitation_step[invited_users][0][_placeholder]'
            placeholderInput.value = ''
            placeholderInput.style.display = 'none'
            formElement.appendChild(placeholderInput)
          }
        })
      }
    } catch (error) {
      console.error('Error connecting user-invitation controller:', error)
    }
  }

  disconnect() {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.addUser()
    }
  }

  addUser(event?: Event) {
    console.log('addUser called', event)
    try {
      console.log('Getting email input value...')
      const email = this.emailInputTarget.value.trim().toLowerCase()
      console.log('Email value:', email)
      
      if (!email || !this.isValidEmail(email)) {
        console.log('Invalid email')
        this.showError(i18n.t('onboarding.user_invitation.enter_valid_email'))
        return
      }

      // Check for duplicates (including current user)
      if (this.invitedUsers.some(user => user.email === email)) {
        console.log('Duplicate email')
        this.showError(i18n.t('onboarding.user_invitation.already_added'))
        return
      }

      // Check if trying to add current user
      if (this.hasCurrentUserEmailValue && email === this.currentUserEmailValue.toLowerCase()) {
        this.showError(i18n.t('onboarding.user_invitation.already_current_user'))
        return
      }

      // Check seat availability before adding
      if (this.hasTotalSeatsValue && this.totalSeatsValue > 0) {
        const currentCount = this.invitedUsers.length + 1 // include current user
        const seatsAfterAdding = currentCount + 1 // include the new user being added
        if (seatsAfterAdding > this.totalSeatsValue) {
          this.showError(i18n.t('onboarding.user_invitation.seat_limit_reached', { count: this.totalSeatsValue }))
          return
        }
      }

      // Determine admin status based on existing_network value
      const isAdmin = this.adminCheckboxTarget.checked || false
      const existingNetwork = this.hasExistingNetworkValue ? this.existingNetworkValue : false

      const newUser = {
        email: email,
        organization_admin: isAdmin, // Always set if admin is checked
        network_admin: isAdmin && !existingNetwork // Only set network admin if creating new network
      }

      this.invitedUsers.push(newUser)
      this.appendUserRow(newUser, this.invitedUsers.length - 1)
      this.updateHiddenInputs()
      this.updateUsersLeft()
      this.clearForm()
      this.triggerAutoSave()
    } catch (error) {
      console.error('Error adding user:', error)
      this.showError(i18n.t('onboarding.user_invitation.invitation_failed'))
    }
  }

  removeUser(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const index = parseInt(button.dataset.index || '0')
    this.invitedUsers.splice(index, 1)
    
    // Remove the row from DOM immediately
    const row = button.closest('[data-onboarding--user-invitation-target="userRow"]')
    if (row) {
      row.remove()
    }
    
    // Re-render all rows with correct indices and update hidden inputs
    this.renderUserRows()
    this.updateHiddenInputs()
    this.updateUsersLeft()
    this.triggerAutoSave()
  }

  skipStep() {
    // Ensure hidden inputs are updated before submitting
    this.updateHiddenInputs()
    const form = document.getElementById('onboarding-form') as HTMLFormElement
    if (form) {
      form.requestSubmit()
    }
  }

  private loadExistingUsers() {
    // Load existing users from the server-rendered data
    const container = document.getElementById('invited-users-list')
    if (!container) return

    const userRows = container.querySelectorAll('[data-onboarding--user-invitation-target="userRow"]:not([data-current-user="true"])')
    const loadedUsers = Array.from(userRows).map((row) => {
      // Get email - need to get first text node, not all text (which might include badge)
      const emailElement = row.querySelector('.font-medium')
      if (!emailElement) return null
      
      // Get text content of first child node (the email text itself)
      const emailText = Array.from(emailElement.childNodes)
        .find(node => node.nodeType === Node.TEXT_NODE)?.textContent?.trim() || ''
      
      // Fallback: if no text node found, get all text and remove badge text
      const email = emailText || emailElement.textContent?.replace('You', '').trim() || ''
      const roles = row.querySelector('.text-sm')?.textContent || ''
      
      if (!email) return null
      
      // Skip if this is the current user
      if (this.hasCurrentUserEmailValue && email.toLowerCase() === this.currentUserEmailValue.toLowerCase()) {
        return null
      }
      
      // Check if user is an admin (either organization or network admin)
      // Note: Since we now only display "Admin" or "Member", we can't determine the specific admin type
      // This is a fallback method - primary loading happens via initialUsersValue which has proper flags
      const isAdmin = roles.includes('Admin') || roles.includes('Organization Admin') || roles.includes('Network Admin')
      
      return {
        email: email,
        // If admin, set organization_admin (network_admin will be determined by existing_network logic on add)
        organization_admin: isAdmin,
        network_admin: false // We can't determine this from DOM, but it will be set correctly when users are added
      }
    }).filter(user => user !== null && user.email)
    
    // Only set if we found users (don't overwrite with empty array)
    if (loadedUsers.length > 0) {
      this.invitedUsers = loadedUsers
      console.log('Loaded existing users:', this.invitedUsers)
    }
  }

  private renderUserRows() {
    const container = document.getElementById('invited-users-list')
    if (!container) return

    // Remove all rows (both server-rendered and dynamically added)
    // We'll re-render everything from the invitedUsers array to ensure consistency
    const allRows = container.querySelectorAll('[data-onboarding--user-invitation-target="userRow"]')
    allRows.forEach(row => row.remove())

    // Re-render current user first
    this.renderCurrentUser()

    // Re-render all invited users
    this.invitedUsers.forEach((user, index) => {
      this.appendUserRow(user, index)
    })
  }

  private appendUserRow(user: any, index: number) {
    const container = document.getElementById('invited-users-list')
    if (!container) return

    const row = this.createUserRow(user, index)
    row.setAttribute('data-dynamic-row', 'true')
    row.setAttribute('data-user-index', index.toString())
    container.appendChild(row) // Append to bottom
  }

  private createUserRow(user: any, index: number, isCurrentUser: boolean = false): HTMLElement {
    const div = document.createElement('div')
    div.className = 'flex items-center justify-between p-3 bg-gray-50 rounded-lg border'
    div.setAttribute('data-onboarding--user-invitation-target', 'userRow')
    if (!isCurrentUser) {
      div.setAttribute('data-index', index.toString())
      div.setAttribute('data-user-index', index.toString())
    } else {
      div.setAttribute('data-current-user', 'true')
    }

    const isAdmin = user.organization_admin || user.network_admin
    const roleText = isAdmin ? i18n.t('onboarding.user_invitation.admin') : i18n.t('onboarding.user_invitation.member')

    const removeButton = isCurrentUser ? '' : `
      <button type="button"
              class="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
              data-action="click->onboarding--user-invitation#removeUser"
              data-index="${index}">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `

    const badge = isCurrentUser ? `<span class="badge badge-primary badge-sm">${i18n.t('onboarding.user_invitation.you')}</span>` : ''

    div.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="flex-1">
          <div class="font-medium text-gray-900">${user.email}</div>
          <div class="text-sm text-gray-500">${roleText}</div>
        </div>
      </div>
      ${isCurrentUser ? badge : removeButton}
    `

    return div
  }

  private renderCurrentUser() {
    if (!this.hasCurrentUserEmailValue || !this.currentUserEmailValue) return

    const container = document.getElementById('invited-users-list')
    if (!container) return

    // Remove existing current user row if it exists
    const existingCurrentUser = container.querySelector('[data-current-user="true"]')
    if (existingCurrentUser) {
      existingCurrentUser.remove()
    }

    // Create current user object
    const currentUser = {
      email: this.currentUserEmailValue,
      organization_admin: true,
      network_admin: true
    }

    // Insert at the top of the list
    const currentUserRow = this.createUserRow(currentUser, -1, true)
    container.insertBefore(currentUserRow, container.firstChild)
  }

  private updateHiddenInputs() {
    const container = this.hiddenInputsTarget
    // Remove all existing inputs (both server-rendered and dynamically created)
    const allInputs = container.querySelectorAll('input[type="hidden"]')
    allInputs.forEach(input => input.remove())

    // Create hidden inputs for all current users
    this.invitedUsers.forEach((user, index) => {
      const emailInput = document.createElement('input')
      emailInput.type = 'hidden'
      emailInput.name = `invited_users[${index}][email]`
      emailInput.value = user.email
      emailInput.setAttribute('data-dynamic-input', 'true')
      container.appendChild(emailInput)

      if (user.organization_admin) {
        const orgAdminInput = document.createElement('input')
        orgAdminInput.type = 'hidden'
        orgAdminInput.name = `invited_users[${index}][organization_admin]`
        orgAdminInput.value = '1'
        orgAdminInput.setAttribute('data-dynamic-input', 'true')
        container.appendChild(orgAdminInput)
      }

      if (user.network_admin) {
        const networkAdminInput = document.createElement('input')
        networkAdminInput.type = 'hidden'
        networkAdminInput.name = `invited_users[${index}][network_admin]`
        networkAdminInput.value = '1'
        networkAdminInput.setAttribute('data-dynamic-input', 'true')
        container.appendChild(networkAdminInput)
      }
    })
  }

  private clearForm() {
    this.emailInputTarget.value = ''
    this.adminCheckboxTarget.checked = false
  }

  private setupAutoSave() {
    // Auto-save is triggered by addUser and removeUser methods
  }

  private triggerAutoSave() {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    this.autoSaveTimeout = window.setTimeout(() => {
      this.performAutoSave()
    }, this.autoSaveDelayValue)
  }

  private async performAutoSave() {
    this.updateHiddenInputs()
    
    const form = document.getElementById('onboarding-form') as HTMLFormElement
    if (!form) {
      console.error('Form not found for auto-save')
      return
    }
    
    // Create FormData from form, but remove _method field to ensure POST
    const formData = new FormData(form)
    formData.delete('_method')
    formData.append('auto_save', 'true')

    try {
      const response = await fetch(this.autoSaveUrlValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      if (response.ok) {
        this.showAutoSaveIndicator()
      } else {
        const error = await response.json()
        this.showError(error.error || i18n.t('onboarding.user_invitation.failed_to_send_invitations'))
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.showError(i18n.t('onboarding.user_invitation.failed_to_send_invitations'))
    }
  }

  private showAutoSaveIndicator() {
    const indicator = this.autoSaveIndicatorTarget
    indicator.style.opacity = '1'
    setTimeout(() => {
      indicator.style.opacity = '0'
    }, 2000)
  }

  private showError(message: string) {
    // Simple error display - you might want to use a toast or modal
    alert(message)
  }

  private updateUsersLeft() {
    try {
      const totalSeats = this.hasTotalSeatsValue ? this.totalSeatsValue : 0
      if (!totalSeats) return
      const currentCount = this.invitedUsers.length + 1 // include current user
      const left = Math.max(totalSeats - currentCount, 0)
      const el = this.element.querySelector('[data-onboarding--user-invitation-target="usersLeft"]') as HTMLElement
      if (el) el.textContent = String(left)
      // Disable add button if no seats left
      if (this.addButtonTarget) {
        this.addButtonTarget.disabled = left <= 0
      }
    } catch (_) { /* no-op */ }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
}
